import { faSignalSlash } from '@fortawesome/pro-solid-svg-icons';
import * as React from 'react';
import { useMemo } from 'react';
import { GoogleAdsRequestTypeGq } from '../../../../../../../graphql/projects/models/GoogleAdsRequestTypeGq';
import { ProjectGq } from '../../../../../../../graphql/projects/models/ProjectGq';
import { projectsApiService } from '../../../../../../../graphql/projects/projectsApiService';
import { CurrentUserGq } from '../../../../../../../graphql/users/models/CurrentUserGq';
import {
  KeywordSearchLimits,
  KeywordSearchSeedMultipliers,
  KeywordSearchWarningLimits,
} from '../../../../../../../logic/otherUtilities/constants/seedInputLimits';
import { formatNumber } from '../../../../../../../logic/otherUtilities/formatting/formatNumber';
import { useGoogleAdsBlockedInfo } from '../../../../../../../logic/otherUtilities/useGoogleAdsBlockedInfo';
import { useRankCapacityCheck } from '../../../../../../../logic/otherUtilities/useRankCapacityCheck';
import VoxelAlert from '../../../../../../voxel/Alert/VoxelAlert';
import VoxelDialog from '../../../../../../voxel/Dialogs/VoxelDialog';
import VoxelDialogActionCancel from '../../../../../../voxel/Dialogs/VoxelDialogActionCancel';
import VoxelDialogActionSubmit from '../../../../../../voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelDialogActions from '../../../../../../voxel/Dialogs/VoxelDialogActions';
import VoxelDialogContent from '../../../../../../voxel/Dialogs/VoxelDialogContent';
import VoxelCheckbox from '../../../../../../voxel/Forms/VoxelCheckbox';
import VoxelLink from '../../../../../../voxel/Link/VoxelLink';
import VoxelReadMore from '../../../../../../voxel/Text/VoxelReadMore';
import VoxelText from '../../../../../../voxel/Text/VoxelText';
import VoxelTooltipShort from '../../../../../../voxel/Tooltip/VoxelTooltipShort';
import VoxelDivider from '../../../../../../voxel/VoxelDivider';
import AddTagsSection from './AddTagsSection';
import LongTailCheckboxes from './LongTailCheckboxes';
import SearchTypeInputFields from './SearchTypeInputFields';
import { SearchRequestTypes, validLongTailLanguages } from './logic/SearchModels';
import { setupSearchFormForDataManagement } from './logic/setupSearchFormForDataManagement';
import { useKeywordCapacityCheck } from '../../../../../../../logic/otherUtilities/useKeywordCapacityCheck';

export interface AddBasicKeywordsFormProps {
  project: ProjectGq;
  user: CurrentUserGq;
  readOnly?: boolean;
  reload: () => Promise<unknown>;
  handleClose: () => void;
  type: GoogleAdsRequestTypeGq;
}

const AddBasicKeywordsForm: React.FC<AddBasicKeywordsFormProps> = ({
  project,
  user,
  readOnly,
  reload,
  handleClose,
  type,
}) => {
  // Google ads blocked request
  const blockedQuery = projectsApiService.queries.googleAds.useGetGoogleAdsBlocked(
    { dummy: '' },
    { pollInterval: 3000 }
  );
  const adsBlockCheck = useGoogleAdsBlockedInfo(blockedQuery?.result?.retryAtTime);

  // Form
  const formik = setupSearchFormForDataManagement(project, reload, handleClose, type);

  // Keyword warning
  const seedCount = useMemo(
    () => formik.values.source?.trim()?.split(/[\n,]+/g)?.length,
    [formik.values.source]
  );
  const seedMultiplier = KeywordSearchSeedMultipliers[formik.values.requestType];
  const keywordWarningLimit = KeywordSearchWarningLimits[formik.values.requestType];
  const keywordSearchLimit = KeywordSearchLimits[formik.values.requestType];

  const keywordCapacity = useMemo(
    () => useKeywordCapacityCheck(user.team, seedCount * seedMultiplier),
    [seedCount]
  );
  const rankCapacity = useMemo(
    () => useRankCapacityCheck(user.team, seedCount * seedMultiplier),
    [seedCount, formik.values.includeRankData]
  );

  const validLongtailLanguage =
    !formik.values.language?.name || validLongTailLanguages.includes(formik.values.language?.name);

  const searchRequestType = SearchRequestTypes.find((t) => t.type === type);

  if (!open) return null;

  return (
    <VoxelDialog
      title={searchRequestType.name}
      open
      onClose={handleClose}
      closeOnBackdrop={false}
      minWClass="min-w-[700px]"
    >
      <VoxelDialogContent classMinWidth="flex flex-col gap-6">
        <VoxelReadMore
          originalText={searchRequestType.shortDescription}
          readMoreText={searchRequestType.moreDescription}
        />
        <div className="w-full flex flex-col gap-3">
          <SearchTypeInputFields formik={formik} readOnly={readOnly} />
          {formik.values.requestType === GoogleAdsRequestTypeGq.LONG_TAIL && (
            <LongTailCheckboxes
              formik={formik}
              validLongtailLanguage={validLongtailLanguage}
              helperText={formik.errors.suggestionsWord}
            />
          )}
        </div>
        <div className="inline-block">
          <VoxelText variant="form-caption">More options</VoxelText>
          <div className="flex flex-row gap-3">
            <VoxelTooltipShort message="Will add rank data to all keywords in the search result">
              <VoxelCheckbox
                label="Add rank data"
                checked={formik.values.includeRankData}
                onChange={(value) => formik.setFieldValue('includeRankData', value)}
                disabled={formik.isSubmitting}
                padding
              />
            </VoxelTooltipShort>
            <VoxelTooltipShort message="Otherwise zero volume keywords will be excluded from the search result">
              <VoxelCheckbox
                label="Include zero volume"
                checked={formik.values.includeZeroVolume}
                onChange={(value) => formik.setFieldValue('includeZeroVolume', value)}
                disabled={formik.isSubmitting}
                padding
              />
            </VoxelTooltipShort>
            {!formik.values.includeTagKeywords && (
              <VoxelTooltipShort message="Tag all keywords in the search result with new or existing tags">
                <VoxelCheckbox
                  label="Tag keywords"
                  checked={formik.values.includeTagKeywords}
                  onChange={(value) => formik.setFieldValue('includeTagKeywords', value)}
                  disabled={formik.isSubmitting}
                  padding
                />
              </VoxelTooltipShort>
            )}
          </div>
        </div>
      </VoxelDialogContent>
      {formik.values.includeTagKeywords && (
        <>
          <VoxelDivider />
          <VoxelDialogContent className="mt-4 flex flex-col gap-2">
            <div className="w-fit">
              <VoxelTooltipShort message="Tag all keywords in the search result with new or existing tags">
                <VoxelCheckbox
                  label="Tag keywords"
                  checked={formik.values.includeTagKeywords}
                  onChange={(value) => formik.setFieldValue('includeTagKeywords', value)}
                  disabled={formik.isSubmitting}
                  padding
                  className="w-fit"
                />
              </VoxelTooltipShort>
            </div>
            <AddTagsSection project={project} formik={formik} disabled={formik.isSubmitting} />
          </VoxelDialogContent>
        </>
      )}
      <VoxelDialogActions>
        <VoxelDialogActionCancel onClick={handleClose} />
        <VoxelDialogActionSubmit
          title="Confirm"
          disabled={
            formik.isSubmitting ||
            !formik.dirty ||
            !formik.isValid ||
            !(formik.values.source.length > 0) ||
            (!keywordCapacity?.keywordSelectionOk &&
              seedCount > 0 &&
              formik.values.requestType !== GoogleAdsRequestTypeGq.SITE) ||
            (!rankCapacity?.rankSelectionOk && formik.values.includeRankData)
          }
          onClick={() => formik.submitForm()}
          submitting={formik.isSubmitting}
        />
      </VoxelDialogActions>
      {adsBlockCheck?.isBlocked && (
        <VoxelAlert
          severity="warning"
          size="sm"
          iconSize="lg"
          roundedClass="rounded-none last:rounded-b-xl"
          fontAIconName={faSignalSlash}
          iconPosition="center"
        >
          Our keyword supplier is experiencing issues. The service is expected to be operational{' '}
          <span className="font-semibold">{adsBlockCheck?.blockedUntil}</span>.
          <br />
          <br />
          You may still queue up keywords.
        </VoxelAlert>
      )}
      {seedCount > keywordWarningLimit && !(seedCount > keywordSearchLimit) && (
        <VoxelAlert
          severity="warning"
          size="sm"
          iconSize="lg"
          roundedClass="rounded-none last:rounded-b-xl"
        >
          Please note that this search may result in up to{' '}
          {formatNumber(seedCount * seedMultiplier)} keywords and that your team has limited
          capacity. Visit{' '}
          <VoxelLink href="/team/usage" openInNewTab openInNewIcon>
            app usage
          </VoxelLink>{' '}
          for more information.
        </VoxelAlert>
      )}
      {!!formik.errors?.maxCharacters?.length && (
        <VoxelAlert
          severity="error"
          size="sm"
          iconSize="lg"
          roundedClass="rounded-none last:rounded-b-xl"
        >
          {formik.errors?.maxCharacters}
        </VoxelAlert>
      )}
      {!!formik.errors?.source?.length && seedCount > keywordSearchLimit && (
        <VoxelAlert
          severity="error"
          size="sm"
          iconSize="lg"
          roundedClass="rounded-none last:rounded-b-xl"
        >
          {formik.errors?.source}
        </VoxelAlert>
      )}
      {!keywordCapacity?.keywordSelectionOk && seedCount > 0 && (
        <VoxelAlert
          severity="error"
          size="sm"
          iconSize="lg"
          roundedClass="rounded-none last:rounded-b-xl"
        >
          This search may result in up to {formatNumber(seedCount * seedMultiplier)} keywords which
          is more than what is left on your teams monthly search volume capacity. Visit{' '}
          <VoxelLink href="/team/usage" openInNewTab openInNewIcon>
            app usage
          </VoxelLink>{' '}
          for more information
        </VoxelAlert>
      )}
      {!rankCapacity?.rankSelectionOk && formik.values.includeRankData && (
        <VoxelAlert
          severity="error"
          size="sm"
          iconSize="lg"
          roundedClass="rounded-none last:rounded-b-xl"
        >
          This search may result in up to {formatNumber(seedCount * seedMultiplier)} keywords which
          is more than what is left on your teams monthly rank capacity. Visit{' '}
          <VoxelLink href="/team/usage" openInNewTab openInNewIcon>
            app usage
          </VoxelLink>{' '}
          for more information
        </VoxelAlert>
      )}
    </VoxelDialog>
  );
};

export default AddBasicKeywordsForm;
